import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import * as Types from 'types';
import { Modal } from 'components/modal';
import { EditionDetails } from 'editions/EditionDetails/EditionDetails';
import { retryIfRejected } from 'utils/helpers';
import { fetchEdition, showGiftEdition } from 'features/editions/actions';
import { EditionMintingFlow } from './EditionMintingFlow/EditionMintingFlow';

type StateProps = {
  edition?: Types.EditionWithArtwork;
};

type DispatchProps = {
  showGiftEdition: typeof showGiftEdition;
  fetchEdition: (id: number) => Promise<Types.Edition | undefined>;
};

type HOCProps = RouteComponentProps<{ id?: string }>;

type Props = { step: '1' | '2' } & StateProps & DispatchProps & HOCProps;

const EditionDetailsComponent: FC<Props> = ({
  edition,
  history,
  showGiftEdition,
  fetchEdition,
  step,
}) => {
  const onRequestClose = () => {
    history.push(Routes.vault_path());
  };

  const onMintClick = () => {
    history.push({ search: '?step=2' });
  };

  useEffect(() => {
    const certificateImageMissing = edition && !edition.certificate.imageUrl;

    if (certificateImageMissing) {
      const certificateImageFetcher = () =>
        new Promise<void>((resolve, reject) => {
          fetchEdition(edition.id).then((edition) => {
            if (edition) {
              edition.certificate.imageUrl ? resolve() : reject();
            } else {
              reject(true);
            }
          });
        });
      retryIfRejected(certificateImageFetcher, 10, 500, true);
    }
  }, [edition]);

  return (
    <Modal
      style={{ maxWidth: '1382px' }}
      isOpen={edition !== undefined}
      onRequestClose={onRequestClose}
      large
      responsive
      className='react-modal--with-border'
    >
      {step === '1' && edition && (
        <EditionDetails
          edition={edition}
          showGiftEdition={showGiftEdition}
          onMintClick={onMintClick}
          onRequestClose={onRequestClose}
        />
      )}
      {step === '2' && edition && (
        <EditionMintingFlow
          edition={edition}
          showGiftEdition={showGiftEdition}
          onRequestClose={onRequestClose}
        />
      )}
    </Modal>
  );
};

const mapStateToProps = ({ editions, artworks }: Types.RootState, props: HOCProps) => {
  let editionWithArtwork: Types.EditionWithArtwork | undefined;
  const activeEditionId =
    props.match.params.id === undefined ? undefined : parseInt(props.match.params.id, 10);

  if (activeEditionId) {
    const edition = editions.byId[activeEditionId];
    const artwork = edition && artworks.byId[edition.artworkId];
    if (edition && artwork) {
      editionWithArtwork = {
        ...edition,
        artwork,
      };
    }
  }

  return {
    edition: editionWithArtwork,
  };
};

const mapDispatchToProps = {
  showGiftEdition,
  fetchEdition,
};

export const EditionDetailsContainer = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditionDetailsComponent),
);
